import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {Mail, MailFolderEnum, WebmailApi,} from "components/src/api/portal/generated";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useWebmailReloadContext} from "enterprise-components/src/context/WebmailReloadContext";
import {Eye as ReadIcon, EyeClosed as UnreadIcon, Mail as MailIcon, Xmark as DeleteIcon,} from "iconoir-react";
import {useCallback, useEffect, useState} from "react";
import {Navbar} from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useLocation, useNavigate} from "react-router-dom";
import {ViewMailPopup} from "./ViewMailPopup";
import "./Messages.scss";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());

interface MessagesProps {
    folder: MailFolderEnum;
}

export const Messages = ({ folder }: MessagesProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation();
    /**
     * The number of available messages
     */
    const [messageCount, setMessageCount] = useState<number>();
    /**
     * The loaded messages
     */
    const [messages, setMessages] = useState<Mail[]>([]);
    /**
     * The messages for which the checkbox is selected
     */
    const [selectedMessages, setSelectedMessages] = useState<Array<Mail>>([]);
    /**
     * The currently selected messages
     */
    const [currentMessage, setCurrentMessage] = useState<Mail | undefined>(
        undefined,
    );
    /**
     * If true, the "confirm delete selected" modal dialog will be shown
     */
    const [showDeleteConfirmSelected, setShowDeleteConfirmSelected] =
        useState<boolean>(false);
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The number of messages to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If true, the reply popup will be shown
     */
    const [showViewMailPopup, setShowViewMailPopup] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const { t } = useTranslationWithOverride();
    const { notifyReload } = useWebmailReloadContext();
    /**
     * If true, only unread messages are shown
     */
    const [unreadOnly, setUnreadOnly] = useState<boolean>(
        location?.state?.unreadOnly ?? false,
    );

    useEffect(() => {
        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter((count) => count + 1);
        }

        webmailApi
            .webmailPortalControllerGetMessageCount(folder, unreadOnly)
            .then((response) => {
                if (superseded) {
                    return;
                }

                setMessageCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter((count) => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [navigate, reloadCounter, setSpinnerStateCounter, t, folder, unreadOnly]);

    useEffect(() => {
        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter((count) => count + 1);
        }

        // reset the selection when we change the page
        setSelectedMessages([]);

        webmailApi
            .webmailPortalControllerGetMessages(
                folder,
                (currentPage - 1) * rowsPerPage,
                rowsPerPage,
                unreadOnly,
            )
            .then((response) => {
                if (superseded) {
                    return;
                }

                const loadedMessages: Mail[] = response.data;

                setMessages(loadedMessages);

                if (loadedMessages.length > 0) {
                    setCurrentMessage(loadedMessages[0]);
                }
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter((count) => count - 1);
                }
                notifyReload();
            });

        return () => {
            superseded = true;
        }
    }, [
        navigate,
        reloadCounter,
        setSpinnerStateCounter,
        t,
        folder,
        currentPage,
        rowsPerPage,
        unreadOnly,
        notifyReload,
    ]);

    const deleteMessages = (messages: Array<Mail>) => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter((count) => count + 1);
        }

        webmailApi
            .webmailPortalControllerDeleteMessages(
                folder,
                messages.map((message) => message.uid),
            )
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter((count) => count - 1);
                }
                setShowDeleteConfirmSelected(false);
                setSelectedMessages([]);
            });
    };

    const onChangeSelect = (checked: boolean, selectedMessage: Mail) => {
        setSelectedMessages(
            checked
                ? [...selectedMessages, selectedMessage]
                : selectedMessages.filter(
                    (message) => message.uid !== selectedMessage.uid,
                ),
        );
    };

    const invertSelection = () => {
        setSelectedMessages(
            [...messages].filter(
                (message) => !selectedMessages.includes(message),
            ),
        );
    };

    const setReadStatus = (read: boolean) => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter((count) => count + 1);
        }

        webmailApi
            .webmailPortalControllerSetReadFlag(
                folder,
                read,
                selectedMessages.map((message) => message.uid),
            )
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter((count) => count - 1);
                }
                setSelectedMessages([]);
            });
    };

    const seenCssClass = (message: Mail) => {
        if (!message.seen) {
            return "tr-font-weight-bold";
        }

        return null;
    };

    const clickedCssClass = (message: Mail) => {
        if (message.uid === currentMessage?.uid) {
            return "tr-secondary";
        }

        return null;
    };

    const onMessageClicked = (message: Mail) => {
        setCurrentMessage(message);
        setShowViewMailPopup(true);
    };

    /**
     * Is called when the view mail popup is closed
     */
    const onViewMailPopupClose = (dialogCloseCause: DialogCloseCause) => {
        setShowViewMailPopup(false);

        if (dialogCloseCause === DialogCloseCause.Succes) {
            reload();
        }
    };

    function reload() {
        forceReload();
    }

    return (
        <>
            <ConfirmDialog
                title={t("common.confirm")}
                bodyText={t("portal.messages.confirm-delete-selected-messages")}
                confirmButtonTitle={t("common.delete")}
                onClickConfirmEvent={() => deleteMessages(selectedMessages)}
                showConfirm={showDeleteConfirmSelected}
                setShowConfirm={setShowDeleteConfirmSelected}
            />

            {showViewMailPopup && currentMessage != undefined && (
                <ViewMailPopup
                    mail={currentMessage}
                    onClose={onViewMailPopupClose}
                />
            )}

            <div className="d-flex flex-column h-100">

                <PageHeader icon={<MailIcon />}
                            title={`${t("portal.messages.title")} / ${folder === MailFolderEnum.Inbox ? t("portal.sidebar.inbox-title") : t("portal.sidebar.sent-title")}`}
                            onClickReload={forceReload}
                />

                <Row>
                    <Col>
                        <RenderAlert
                            message={errorMessage}
                            setMessage={setErrorMessage}
                            tooltipPlacement={"bottom"}
                        />
                    </Col>
                </Row>

                <div className="px-4">
                    <Row className="mb-2">
                        <Col>
                            <Form.Check
                                type="checkbox"
                                id="unread-only"
                                label={t("portal.messages.unread-only")}
                                checked={unreadOnly}
                                onChange={(e) => {
                                    setUnreadOnly(e.target.checked);
                                    setCurrentPage(1);
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100">
                            <div className="cm-messages-table-wrap h-100">
                                <Table className="cm-messages-table sticky sticky-x" hover>
                                    <thead className="sticky-top">
                                    <tr className="text-nowrap">
                                        {selectedMessages.length === 0 ? (
                                            <>
                                                <th className="th-w-min">
                                                    <InvertSelection
                                                        invertSelectionEvent={
                                                            invertSelection
                                                        }
                                                    />
                                                </th>
                                                <th className="cm-col-subject">
                                                    {t(
                                                        "portal.messages.subject",
                                                    )}
                                                </th>
                                                {folder ===
                                                    MailFolderEnum.Inbox && (
                                                        <th className="cm-col-secondary">
                                                            {t(
                                                                "portal.messages.from",
                                                            )}
                                                        </th>
                                                    )}
                                                {folder ===
                                                    MailFolderEnum.Sent && (
                                                        <th className="cm-col-secondary">
                                                            {t(
                                                                "portal.messages.to",
                                                            )}
                                                        </th>
                                                    )}
                                                <th className="cm-col-secondary">
                                                    {t(
                                                        "portal.messages.date",
                                                    )}
                                                </th>
                                                <th className="cm-col-secondary">
                                                    {t(
                                                        "portal.messages.size",
                                                    )}
                                                </th>
                                                <th>
                                                    {t(
                                                        "portal.messages.id",
                                                    )}
                                                </th>
                                            </>
                                        ) : (
                                            <th colSpan={6}>
                                                <Navbar className="bg-body-tertiary ps-2">
                                                    {/* Add icon to first column which will clear the selection when clicked */}
                                                    <ClearSelection
                                                        clearSelectionEvent={() =>
                                                            setSelectedMessages(
                                                                [],
                                                            )
                                                        }
                                                        checked={true}
                                                    />

                                                    <Navbar.Text className="ms-2">
                                                        {
                                                            selectedMessages.length
                                                        }{" "}
                                                        selected
                                                    </Navbar.Text>

                                                    <div className="vr ms-4 me-2"></div>

                                                    <Nav>
                                                        <Nav.Link
                                                            disabled={
                                                                selectedMessages.length ===
                                                                0
                                                            }
                                                            onClick={() =>
                                                                setShowDeleteConfirmSelected(
                                                                    true,
                                                                )
                                                            }>
                                                            <DeleteIcon className="text-danger" />{" "}
                                                            {t(
                                                                "portal.messages.delete-selected",
                                                            )}
                                                        </Nav.Link>

                                                        <Nav.Link
                                                            disabled={
                                                                selectedMessages.length ===
                                                                0
                                                            }
                                                            onClick={() =>
                                                                setReadStatus(
                                                                    true,
                                                                )
                                                            }>
                                                            <ReadIcon className="text-success" />{" "}
                                                            {t(
                                                                "portal.messages.mark-as-read",
                                                            )}
                                                        </Nav.Link>

                                                        <Nav.Link
                                                            disabled={
                                                                selectedMessages.length ===
                                                                0
                                                            }
                                                            onClick={() =>
                                                                setReadStatus(
                                                                    false,
                                                                )
                                                            }>
                                                            <UnreadIcon className="text-success" />{" "}
                                                            {t(
                                                                "portal.messages.mark-as-unread",
                                                            )}
                                                        </Nav.Link>
                                                    </Nav>
                                                </Navbar>
                                            </th>
                                        )}
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {messages.map((message: Mail) => (
                                        // make the tr focusable and execute on enter key
                                        <tr
                                            key={message.uid}
                                            className={`text-nowrap ${seenCssClass(message)} ${clickedCssClass(message)}`}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) =>
                                                Utils.clickOnEnterKeyPressed(
                                                    e,
                                                    () =>
                                                        onMessageClicked(
                                                            message,
                                                        ),
                                                )
                                            }>
                                            <td className="td-w-min">
                                                <Form.Check
                                                    type="checkbox"
                                                    className="me-0"
                                                    checked={selectedMessages.includes(
                                                        message,
                                                    )}
                                                    onChange={(e) =>
                                                        onChangeSelect(
                                                            e.target
                                                                .checked,
                                                            message,
                                                        )
                                                    }
                                                    inline
                                                />
                                            </td>

                                            <td
                                                className="cm-col-subject"
                                                onClick={() =>
                                                    onMessageClicked(
                                                        message,
                                                    )
                                                }>
                                                <Truncate
                                                    value={message.subject}
                                                    maxLength={120}
                                                />
                                            </td>

                                            {folder ===
                                                MailFolderEnum.Inbox && (
                                                    <td
                                                        className="cm-col-secondary"
                                                        onClick={() =>
                                                            onMessageClicked(
                                                                message,
                                                            )
                                                        }>
                                                        <Truncate
                                                            value={StringUtils.unquoteEmail(
                                                                message.from,
                                                            )}
                                                            maxLength={60}
                                                        />
                                                    </td>
                                                )}

                                            {folder ===
                                                MailFolderEnum.Sent && (
                                                    <td
                                                        className="cm-col-secondary"
                                                        onClick={() =>
                                                            onMessageClicked(
                                                                message,
                                                            )
                                                        }>
                                                        <Truncate
                                                            value={message.to
                                                                ?.map((e) =>
                                                                    StringUtils.unquoteEmail(
                                                                        e,
                                                                    ),
                                                                )
                                                                .join(", ")}
                                                            maxLength={60}
                                                        />
                                                    </td>
                                                )}

                                            <td
                                                className="cm-col-secondary"
                                                onClick={() =>
                                                    onMessageClicked(
                                                        message,
                                                    )
                                                }>
                                                {DateUtils.fromTimestampToLocaleDateStringShort(
                                                    message.date,
                                                )}
                                            </td>
                                            <td
                                                className="cm-col-secondary"
                                                onClick={() =>
                                                    onMessageClicked(
                                                        message,
                                                    )
                                                }>
                                                {Utils.formatBytes(message.size)}
                                            </td>
                                            <td
                                                onClick={() =>
                                                    onMessageClicked(
                                                        message,
                                                    )
                                                }>
                                                {message.uid}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage} maxRowsPerPage={40}/>}
                    right={<Pager itemCount={messageCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
};
